import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DashboardApi } from "../api.js";

export interface DashboardSessionState {
  sessionId: string;
  agentId: string;
}

/**
 * The dashboard chats as the user's general agent through one server-managed session.
 * The id lives in memory only, is fetched once per unlock (StrictMode double effects
 * share the same in-flight promise), and is dropped when the cockpit locks.
 */
export function useDashboardSession(api: DashboardApi) {
  const [state, setState] = useState<DashboardSessionState | null>(null);
  const inflight = useRef<Promise<DashboardSessionState> | null>(null);

  const ensure = useCallback(async (): Promise<DashboardSessionState> => {
    if (state) return state;
    if (!inflight.current) {
      inflight.current = api.dashboardSession()
        .then((result) => {
          const next = { sessionId: result.session.id, agentId: result.generalAgent.id };
          api.setSession(next.sessionId);
          setState(next);
          return next;
        })
        .finally(() => { inflight.current = null; });
    }
    return inflight.current;
  }, [api, state]);

  useEffect(() => () => { api.setSession(null); }, [api]);

  return useMemo(() => ({ session: state, ensure }), [state, ensure]);
}
